import {
    pendingTask,
    begin,
    end,
} from 'react-redux-spinner';
import { toastr } from 'react-redux-toastr';
import { EDIT_BID_SELECTION, UPDATE_SELECTION_FILTERS } from './bidSelection.actions';
import {
    EmployeeAutoNumberingType,
} from '../../Enums';
import { LOADING_START, LOADING_END } from '../loading';
import * as onBehalfApi from '../../Manager/services/api-endpoints/behalf.service';
import { UPDATE_EMPLOYEE_SUBMISSION_STATUS, calculateAwarding } from '../../Manager/store/bidProfileManagement';
import { parseEmployeeBidProfileServerModel, bidSelectionToMap } from '../../Employee/services/utilities/employeeBidProfileModelMapper';
import { UPDATE_EMPLOYEE_BIDDABLE_PROFILE } from '../../Employee/store/bidProfile/bidProfile.actions';
import { autoSaveBidSelectionDraft } from '../../Employee/store/bidProfile/bidProfile.middlewares';

/**
 * @param {boolean} checked
 * @param {import('redux').Dispatch} dispatch
 */
export const updateShowWeeklyHours = (checked, dispatch) => dispatch({
    type: UPDATE_SELECTION_FILTERS,
    payload: {
        showTotalHours: checked,
    },
});

/**
 * @param {ApiEmployee.IGroupedScheduleItemPresentationModel[]} items
 * @param {string[]} selections
 * @param {string | null} approvedItemId
 * @param {boolean} isGroupShiftlinesWithSameSchedules
 * @returns {EmployeeBidProfile.IGroupedScheduleTemplateItems[]}
 */
const prepareTableItems = (items, selections, approvedItemId, isGroupShiftlinesWithSameSchedules) => items.map((item, index) => {
    const selectedId = isGroupShiftlinesWithSameSchedules ? item.groupId : item.id.toString();
    const selectionIndex = selections.indexOf(selectedId);
    const isSelected = selectionIndex !== -1;
    const weeklyHeight = Object.keys(item.maxDayPerWeek).map((x) => item.maxDayPerWeek[x]);
    /** @type {BidProfile.ISchedulePatternTemplateItem[][][]} */
    const shifts = [];
    item.schedulePatternTemplate.items.forEach((shift) => {
        const week = Math.floor((shift.day - 1) / 7);
        const dayOfWeek = (shift.day - 1) % 7;
        if (shifts[week] === undefined) {
            shifts[week] = [[], [], [], [], [], [], []];
        }

        shifts[week][dayOfWeek].push(shift);
    });

    return {
        ...item,
        weeklyHeight,
        shifts,
        isSelected,
        position: isSelected ? selectionIndex : 1000000 + index,
        tableOrder: -1, // TODO : make these obsolete
        rowClassName: '', // TODO : make these obsolete
        approvedRow: isGroupShiftlinesWithSameSchedules
            ? item.groupId === approvedItemId
            : item.id.toString() === approvedItemId,
    };
});

/**
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} items
 * @returns {number}
 */
const calculateRowHeight = (items) => {
    const shiftlineSelectionRowPadding = 10;
    const shiftlineSelectionShiftHeight = 33;
    const shiftlineSelectionMinRowHeight = 40;

    const maxNbWeek = Math.max(...items.map((x) => x.nbWeek));
    const sumOfWeeklyMax = items.map((x) => x.weeklyHeight.reduce((prev, curr) => prev + (curr || 1), 0));
    const maxHeightUnits = Math.max(maxNbWeek, ...sumOfWeeklyMax);
    const calculatedRowHeight = shiftlineSelectionRowPadding + (maxHeightUnits * shiftlineSelectionShiftHeight);
    return Math.max(calculatedRowHeight, shiftlineSelectionMinRowHeight);
};

/**
 * @param {ApiEmployee.IGroupedScheduleItemPresentationModel[]} items
 * @param {EmployeeBidProfile.IEmployeeBidSelectionMap} employeeBidSelection
 * @param {DayOfWeek} anchorDay
 * @param {ScheduleSource} scheduleSource
 * @param {BidProfile.ISchedulePatternsShiftTemplateDictionary} shifts
 * @param {BidProfile.ISchedulePatternsPayCodeDictionary} paycodes
 * @param {number} dayCount
 * @param {PeriodType} periodType
 * @param {String | null} approvedItemGroupId
 * @param {boolean} isGroupShiftlinesWithSameSchedules
 */
export const setInitSelectionValues = (
    items,
    employeeBidSelection,
    anchorDay,
    scheduleSource,
    shifts,
    paycodes,
    dayCount,
    periodType,
    approvedItemGroupId = null,
    isGroupShiftlinesWithSameSchedules = true,
) =>
    /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        const selections = Object.values(employeeBidSelection?.selection || {});
        const preparedItems = prepareTableItems(items, selections, approvedItemGroupId, isGroupShiftlinesWithSameSchedules);
        const rowHeight = calculateRowHeight(preparedItems);

        dispatch({
            type: EDIT_BID_SELECTION,
            payload: {
                items: preparedItems,
                employeeSelections: selections,
                isDraft: employeeBidSelection?.isDraft || false,
                rowHeight,
                anchorDay,
                scheduleSource,
                shifts,
                paycodes,
                dayCount,
                periodType,
            },
        });

        /**
         * @type {string[]}
         */
        const locations = [];
        /**
         * @type {string[]}
         */
        const jobs = [];
        /**
         * @type {string[]}
         */
        const workplans = [];

        for (const item of items) {
            if (!locations.includes(item.primaryLocation)) {
                locations.push(item.primaryLocation);
            }
            for (const transferLocation of item.transferLocations) {
                if (!locations.includes(transferLocation)) {
                    locations.push(transferLocation);
                }
            }

            if (!jobs.includes(item.primaryJob)) {
                jobs.push(item.primaryJob);
            }
            for (const transferJob of item.transferJobs) {
                if (!jobs.includes(transferJob)) {
                    jobs.push(transferJob);
                }
            }

            if (item.workplanName) {
                if (!workplans.includes(item.workplanName)) {
                    workplans.push(item.workplanName);
                }
            }
        }

        dispatch({
            type: UPDATE_SELECTION_FILTERS,
            payload: {
                isLocationIncludeTransfer: true,
                locationItems: locations,
                isJobIncludeTransfer: true,
                jobItems: jobs,
                workplanItems: workplans,
            },
        });
    };

/**
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} items
 * @param {string[]} newSelection
 * @param {boolean} isGroupShiftlinesWithSameSchedules
 */
export const updateSelection = (items, newSelection, isGroupShiftlinesWithSameSchedules) =>
    /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        const preparedItems = items.map((item, index) => {
            const selectedId = isGroupShiftlinesWithSameSchedules ? item.groupId : item.id.toString();
            const selectionIndex = newSelection.indexOf(selectedId);
            const isSelected = selectionIndex !== -1;
            const position = isSelected ? selectionIndex : 1000000 + index;
            return {
                ...item,
                isSelected,
                position,
            };
        }).sort((a, b) => a.position - b.position);

        dispatch({
            type: EDIT_BID_SELECTION,
            payload: {
                items: preparedItems,
                employeeSelections: newSelection,
            },
        });

        if (newSelection.length > 0) {
            dispatch(autoSaveBidSelectionDraft());
        }
    };

/**
 * @param {EmployeeBidSelection.IBidSelectionStore} storeData
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} source
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} target
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} items
 * @param {boolean} isGroupShiftlinesWithSameSchedules
 */
export const handleMoveRow = (storeData, source, target, items, isGroupShiftlinesWithSameSchedules) =>
    /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        if ((isGroupShiftlinesWithSameSchedules && source.groupId === target.groupId)
            || (!isGroupShiftlinesWithSameSchedules && source.id === target.id)) { // Cannot drag and drop with itself
            return;
        }
        if (source.isSelected !== target.isSelected) { // Only blue squares can switch with white squares
            return;
        }
        const findSourceCondition = isGroupShiftlinesWithSameSchedules ? (x) => x.groupId === source.groupId : (x) => x.id === source.id;
        const findTargetCondition = isGroupShiftlinesWithSameSchedules ? (x) => x.groupId === target.groupId : (x) => x.id === target.id;

        const sourceIndex = items.findIndex(findSourceCondition);
        const targetIndex = items.findIndex(findTargetCondition);

        const isMoveUp = sourceIndex > targetIndex;

        const targetPos = items[targetIndex].position;
        if (isMoveUp) {
            for (let i = targetIndex; i <= sourceIndex; i++) {
                if (i === sourceIndex) {
                    items[i].position = targetPos;
                } else {
                    items[i].position = items[i + 1].position;
                }
            }
        } else {
            for (let i = targetIndex; i >= sourceIndex; i--) {
                if (i === sourceIndex) {
                    items[i].position = targetPos;
                } else {
                    items[i].position = items[i - 1].position;
                }
            }
        }
        const preparedItems = [...storeData.items].sort((a, b) => a.position - b.position);
        const selections = preparedItems
            .filter((x) => x.isSelected)
            .map((x) => (isGroupShiftlinesWithSameSchedules ? x.groupId : x.id.toString()));

        dispatch({
            type: EDIT_BID_SELECTION,
            payload: {
                items: preparedItems,
                employeeSelections: selections,
            },
        });

        if (selections.length > 0) {
            dispatch(autoSaveBidSelectionDraft());
        }
    };

/**
 * @param {EmployeeBidSelection.IBidSelectionStore} storeData
 * @param {string} autoNumberingType
 * @param { EmployeeBidProfile.IGroupedScheduleTemplateItems[] } visibleItems
 * @param {boolean} isGroupShiftlinesWithSameSchedules
 */
export const handleAutoOrderSelected = (storeData, autoNumberingType, visibleItems, isGroupShiftlinesWithSameSchedules) =>
    /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        /** @type {(string | number)[]} */
        let selections = [];
        if (autoNumberingType === EmployeeAutoNumberingType.Add) {
            selections = [...storeData.employeeSelections];
            let lastPos = storeData.items.slice().reverse().find((x) => x.isSelected)?.position;
            if (!lastPos) {
                lastPos = 1;
            }

            const selectionsToAdd = visibleItems.map((x) => {
                if (!x.isSelected) {
                    x.isSelected = true;
                    // @ts-ignore
                    x.position = ++lastPos;
                    return isGroupShiftlinesWithSameSchedules ? x.groupId : x.id.toString();
                }
                return null;
            }).filter((x) => x !== null);
            // @ts-ignore
            selections.push(...selectionsToAdd);
        } else if (autoNumberingType === EmployeeAutoNumberingType.Overwrite) {
            let lastPos = 1000000;
            storeData.items.forEach((x) => {
                x.isSelected = false;
                x.position = ++lastPos;
            });

            lastPos = 1;
            selections = visibleItems.map((x) => {
                x.isSelected = true;
                x.position = ++lastPos;
                return isGroupShiftlinesWithSameSchedules ? x.groupId : x.id.toString();
            });
        }

        dispatch({
            type: EDIT_BID_SELECTION,
            payload: {
                items: [...storeData.items].sort((a, b) => a.position - b.position),
                employeeSelections: selections,
            },
        });

        if (selections.length > 0) {
            dispatch(autoSaveBidSelectionDraft());
        }
    };

/**
 * @param {EmployeeBidSelection.IBidSelectionStore} storeData
 * @param {boolean} isPreferential
 * @param {boolean} isGroupShiftlinesWithSameSchedules
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} rowData
 */
export const handleToggleSelected = (storeData, isPreferential, isGroupShiftlinesWithSameSchedules, rowData) =>
    /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        let selections = [...storeData.employeeSelections];
        const selectedId = isGroupShiftlinesWithSameSchedules ? rowData.groupId : rowData.id.toString();
        const index = selections.indexOf(selectedId);
        if (isPreferential) {
            if (index === -1) {
                selections.push(selectedId);
                const lastPos = storeData.items.slice().reverse().find((x) => x.isSelected)?.position;
                rowData.position = lastPos ? lastPos + 1 : 1;
                rowData.isSelected = true;
            } else {
                selections.splice(index, 1);
                const lastPos = storeData.items.find((x) => !x.isSelected)?.position;
                rowData.position = lastPos ? lastPos - 1 : 1000000;
                rowData.isSelected = false;
            }
        } else {
            storeData.items.forEach((x, i) => {
                x.isSelected = false;
                x.position = 1000000 + i;
            });
            if (index === -1) {
                selections = [selectedId];
                rowData.position = 1;
                rowData.isSelected = true;
            } else {
                selections = [];
            }
        }

        dispatch({
            type: EDIT_BID_SELECTION,
            payload: {
                items: [...storeData.items].sort((a, b) => a.position - b.position),
                employeeSelections: selections,
            },
        });

        if (selections.length > 0) {
            dispatch(autoSaveBidSelectionDraft());
        }
    };

/**
 * @param {string} profileId
 * @param {number} employeeId
 * @param {Function} onError
 */
export const loadEmployeeBehalfShiftlineSelection = (profileId, employeeId, onError) =>
    /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        if (!employeeId) {
            return Promise.reject;
        }
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        try {
            const result = await onBehalfApi.EmployeeView(profileId, employeeId);
            if (result.status === 200) {
                const bidProfile = result.data;

                // @ts-ignore
                dispatch(setInitSelectionValues(
                    bidProfile.items,
                    bidProfile.employeeBidSelection || { selection: {}, isDraft: true, employeeId, completionError: '' },
                    bidProfile.anchorDay,
                    bidProfile.scheduleSource,
                    bidProfile.shifts,
                    bidProfile.paycodes,
                    bidProfile.dayCount,
                    bidProfile.periodType,
                    null,
                    bidProfile.isGroupShiftlinesWithSameSchedules,
                ));
                const parsedBidProfile = parseEmployeeBidProfileServerModel(result);
                updateShowWeeklyHours(parsedBidProfile.weeklyHoursByDefault, dispatch);

                // @ts-ignore
                return dispatch({
                    type: UPDATE_EMPLOYEE_BIDDABLE_PROFILE,
                    payload: parsedBidProfile,
                });
            }
            onError();
            toastr.error('Fetching bid information', 'Failed to fetch');
            return Promise.reject;
        } catch (error) {
            onError();
            toastr.error('Fetching bid information', error.response.data.Message);
            return Promise.reject;
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };

/**
 * @param {string} profileId
 * @param {number} employeeId
 * @param {string[]} employeeSelections
 */
export const bidOnBehalfOfEmployee = (profileId, employeeId, employeeSelections) =>
/** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        try {
            if (!employeeSelections.length) {
                toastr.error('Saving selection', 'Please choose at least one selection');
                return Promise.reject;
            }
            dispatch({ type: LOADING_START, [pendingTask]: begin });

            const response = await onBehalfApi.SubmitSelection(
                profileId,
                employeeId,
                { selections: bidSelectionToMap(employeeSelections) },
            );
            if (response.status === 200) {
                const bidProfile = parseEmployeeBidProfileServerModel(response);

                dispatch({
                    type: UPDATE_EMPLOYEE_BIDDABLE_PROFILE,
                    payload: bidProfile,
                });
                toastr.success('Bid has been submitted with success', '');
                return dispatch({
                    type: UPDATE_EMPLOYEE_SUBMISSION_STATUS,
                    payload: {
                        employeeId,
                        data: {
                            status: bidProfile.employeeSubmissionStatus,
                            submittedBy: bidProfile.submittedBy,
                            submittedOn: bidProfile.employeeSubmissionDate,
                        },
                    },
                });
            }
            toastr.error('Submitting bid', 'Failed to save');
            return Promise.reject;
        } catch (error) {
            toastr.error('Submitting bid', error.response.data.Message);
            return Promise.reject;
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };

/**
 * @param {string} profileId
 * @param {number} employeeId
 */
export const declineOnBehalfBidSelection = (profileId, employeeId) =>
/** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        try {
            dispatch({ type: LOADING_START, [pendingTask]: begin });

            const response = await onBehalfApi.Decline(profileId, employeeId);
            if (response.status === 200) {
                dispatch({
                    type: EDIT_BID_SELECTION,
                    payload: {
                        employeeSelections: [],
                    },
                });

                const bidProfile = parseEmployeeBidProfileServerModel(response);
                dispatch({
                    type: UPDATE_EMPLOYEE_BIDDABLE_PROFILE,
                    payload: bidProfile,
                });
                dispatch({
                    type: UPDATE_EMPLOYEE_SUBMISSION_STATUS,
                    payload: {
                        employeeId,
                        data: {
                            status: bidProfile.employeeSubmissionStatus,
                            submittedBy: bidProfile.submittedBy,
                            submittedOn: bidProfile.employeeSubmissionDate,
                        },
                    },
                });
                toastr.success('Declining employee\'s bid selection', 'Bid employees declined successfully');
            } else {
                toastr.error('Declining employee\'s bid selection', 'Failed to decline');
            }
        } catch (error) {
            toastr.error('Declining employee\'s bid selection', error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };
